import dayjs, { Dayjs } from "dayjs";

// Display-only date logic for blog posts. The "last updated" date is picked the
// same way blogSchema.ts picks dateModified (`updated`, then `updatedAt`), so the
// date readers see matches the one Google reads. `updatedAt` alone is not used
// first because bulk sweeps (e.g. the 31 Aug 2026 dash cleanup, 399 posts)
// bumped it without touching `updated`.

function parseBlogDate(value?: string | Date): Dayjs | null {
  if (!value) return null;
  let parsed = dayjs(value);
  if (!parsed.isValid() && typeof value === "string") {
    // legacy D/M/YYYY strings (e.g. "20/1/2025")
    const m = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
      parsed = dayjs(
        `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`
      );
    }
  }
  return parsed.isValid() ? parsed : null;
}

export interface IBlogDisplayDates {
  /** Date to lead with: the last update when there is one, else the publish date. */
  primary: { iso: string; label: string; isUpdate: boolean };
  /** Original publish date, only set when `primary` is an update. */
  published?: { iso: string; label: string };
}

/**
 * Updated date leads, first-published date follows. A post with no update, or
 * one updated on the same calendar day it was published, shows one date only.
 */
export function getBlogDisplayDates(post: {
  publishedDate: string;
  updated?: string | Date;
  updatedAt?: string;
}): IBlogDisplayDates {
  const published = dayjs(post.publishedDate);
  const updated = parseBlogDate(post.updated || post.updatedAt);
  const publishedOut = {
    iso: post.publishedDate,
    label: published.format("MMM DD, YYYY"),
  };

  if (!updated || !updated.isAfter(published, "day")) {
    return { primary: { ...publishedOut, isUpdate: false } };
  }

  return {
    primary: {
      iso: updated.format("YYYY-MM-DD"),
      label: updated.format("MMM DD, YYYY"),
      isUpdate: true,
    },
    published: publishedOut,
  };
}
